import * as React from 'react'
import { Check, Loader2 } from 'lucide-react'
import { OAUTH_SCOPE_LABELS } from '@lib/constants/oauth-scopes'
import type { OauthGrantDto } from '@lib/dto/oauth-consent.dto'
import { formatDate } from '@lib/helpers/format'
import { changePasswordInputSchema } from '@lib/input/auth.input'
import { PASSWORD_MIN_LENGTH } from '@lib/validators/password.validator'
import { useAuth } from '@/auth/AuthContext'
import { useChangePassword } from '@/hooks/useChangePassword'
import { useOauthGrants, useRevokeOauthGrant } from '@/hooks/useOauth'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/**
 * Your own account.
 *
 * Separate from Settings, which is organization-wide: the tree labor rate one
 * person edits changes what every quote in the business says, while a password
 * belongs to exactly one person and nobody else can even see it. Sharing a page
 * put a personal control under a heading that read "applies to everyone at
 * Smithwell Organics", which is the opposite of what it does.
 *
 * Reached from the signed-in address in the top bar rather than from the main
 * nav — it is where someone looks for their own things, and the nav is for
 * areas of the product.
 */
export function AccountPage() {
  const { user } = useAuth()

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-xl font-semibold">Your account</h1>

      <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
        <h2 className="text-lg font-semibold">Profile</h2>
        {/*
          Read-only for now. Names and addresses are set when an invite is
          accepted and there is no endpoint to change either; showing them as
          disabled inputs would promise an edit that does not exist.
        */}
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[8rem_1fr]">
          <dt className="text-muted-text">Name</dt>
          <dd className="truncate">{user?.name ?? '—'}</dd>
          <dt className="text-muted-text">Email</dt>
          <dd className="truncate">{user?.email ?? '—'}</dd>
        </dl>
      </section>

      <ConnectedAppsSection />

      <PasswordSection />
    </div>
  )
}

/** Turn a disconnect failure into a sentence. The list stays so they can retry. */
function disconnectErrorMessage<E>(error: E): string {
  if (isApiError(error)) {
    return error.body.message ?? 'Could not disconnect that app.'
  }
  return toErrorMessage(error)
}

function connectedAppTitle(grant: OauthGrantDto): string {
  const name = grant.clientName.trim()
  if (name === '') return grant.redirectHost
  return name
}

/**
 * Apps this person has allowed to act as them.
 *
 * On the account page, next to the password, because a grant belongs to this
 * login. Settings is the organization's; a connected app is not.
 */
function ConnectedAppsSection() {
  const grants = useOauthGrants()
  const revoke = useRevokeOauthGrant()
  const [revokingId, setRevokingId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  async function handleDisconnect(id: string): Promise<void> {
    setError(null)
    setRevokingId(id)
    try {
      await revoke.mutateAsync(id)
    } catch (caught) {
      setError(disconnectErrorMessage(caught))
    } finally {
      setRevokingId(null)
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h2 className="text-lg font-semibold">Connected apps</h2>
        <p className="mt-0.5 text-sm text-muted-text">
          Apps allowed to work in Smithwell as you.
        </p>
      </div>
      {error !== null ? <p className="text-sm text-danger">{error}</p> : null}
      <ConnectedAppsList
        isPending={grants.isPending}
        isError={grants.isError}
        grants={grants.data?.grants ?? []}
        revokingId={revokingId}
        onDisconnect={handleDisconnect}
      />
    </section>
  )
}

interface ConnectedAppsListProps {
  isPending: boolean
  isError: boolean
  grants: OauthGrantDto[]
  revokingId: string | null
  onDisconnect: (id: string) => void
}

function ConnectedAppsList({
  isPending,
  isError,
  grants,
  revokingId,
  onDisconnect,
}: ConnectedAppsListProps) {
  if (isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-text">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading connected apps…
      </p>
    )
  }
  if (isError) {
    return (
      <p className="text-sm text-danger">
        Could not load connected apps. Refresh to try again.
      </p>
    )
  }
  if (grants.length === 0) {
    return <p className="text-sm text-muted-text">No apps connected.</p>
  }

  return (
    <ul className="divide-y divide-default">
      {grants.map((grant) => (
        <li key={grant.id} className="py-3 first:pt-0 last:pb-0">
          <GrantRow
            grant={grant}
            disconnecting={revokingId === grant.id}
            disabled={revokingId !== null}
            onDisconnect={onDisconnect}
          />
        </li>
      ))}
    </ul>
  )
}

interface GrantRowProps {
  grant: OauthGrantDto
  disconnecting: boolean
  disabled: boolean
  onDisconnect: (id: string) => void
}

function GrantRow({
  grant,
  disconnecting,
  disabled,
  onDisconnect,
}: GrantRowProps) {
  const title = connectedAppTitle(grant)

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 break-words text-sm font-medium">{title}</p>
        {/*
          The button is the confirmation. A second dialog would ask the same
          question again for an action that only removes this person's grant.
        */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={disabled}
          aria-label={`Disconnect ${title}`}
          onClick={() => {
            void onDisconnect(grant.id)
          }}
        >
          {disconnecting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : null}
          Disconnect
        </Button>
      </div>
      <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[8rem_1fr]">
        <dt className="text-muted-text">Returns to</dt>
        <dd className="break-all">{grant.redirectHost}</dd>
        {grant.scopes.length === 0 ? null : (
          <>
            <dt className="text-muted-text">Allowed</dt>
            <dd>
              <ul className="space-y-1">
                {grant.scopes.map((scope) => (
                  <li key={scope}>{OAUTH_SCOPE_LABELS[scope]}</li>
                ))}
              </ul>
            </dd>
          </>
        )}
        <dt className="text-muted-text">Connected</dt>
        <dd>{formatDate(grant.createdAt)}</dd>
        <dt className="text-muted-text">Expires</dt>
        <dd>{formatDate(grant.expiresAt)}</dd>
      </dl>
    </div>
  )
}

/**
 * Change your own password.
 *
 * Before this existed there was no way to change a password at all: one is set
 * when an invite is accepted and never again, so a forgotten or weak password
 * meant a shell into the production database.
 */
function PasswordSection() {
  const { user } = useAuth()
  const changePassword = useChangePassword()

  const [current, setCurrent] = React.useState('')
  const [next, setNext] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [changed, setChanged] = React.useState(false)

  /*
   * The confirmation field is checked here and nowhere else. It is not part of
   * the shared input schema because the API has no use for it — it guards
   * against a typo in a field nobody can read back, which is a property of this
   * form rather than of the request.
   */
  const mismatch = confirm !== '' && next !== confirm
  const parsed = changePasswordInputSchema.safeParse({
    currentPassword: current,
    newPassword: next,
  })
  const canSubmit =
    parsed.success && !mismatch && confirm !== '' && !changePassword.isPending

  function fieldIssue(field: 'currentPassword' | 'newPassword'): string | null {
    if (parsed.success) return null
    // Only complain about a field once it has been typed in; an untouched form
    // covered in red is noise, not guidance.
    const touched = field === 'currentPassword' ? current !== '' : next !== ''
    if (!touched) return null
    return (
      parsed.error.issues.find((issue) => issue.path[0] === field)?.message ??
      null
    )
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setChanged(false)
    if (!parsed.success) return

    try {
      await changePassword.mutateAsync(parsed.data)
      // Clear on success so the new password is not left sitting in the DOM.
      setCurrent('')
      setNext('')
      setConfirm('')
      setChanged(true)
    } catch (caught) {
      setError(
        isApiError(caught)
          ? (caught.body.message ?? 'Could not change your password.')
          : toErrorMessage(caught),
      )
    }
  }

  const newPasswordIssue = fieldIssue('newPassword')

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h2 className="text-lg font-semibold">Password</h2>
        <p className="mt-0.5 text-sm text-muted-text">
          Change your password. You stay signed in here and on your other
          devices.
        </p>
      </div>

      <form
        className="max-w-xs space-y-3"
        onSubmit={(event) => {
          void handleSubmit(event)
        }}
      >
        {/*
          A hidden username field so a password manager knows WHICH account
          these fields belong to. Without one it either declines to offer the
          new password or files it under a guess; the browser warns about
          exactly this. Read-only and out of the tab order — it is here for
          software, not for the person filling the form in.
        */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={user?.email ?? ''}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
        />

        <div className="space-y-1.5">
          <Label htmlFor="current-password">Current password</Label>
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            value={current}
            aria-invalid={fieldIssue('currentPassword') !== null}
            onChange={(event) => {
              setCurrent(event.target.value)
              setChanged(false)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="new-password">New password</Label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={next}
            aria-invalid={newPasswordIssue !== null}
            aria-describedby="new-password-help"
            onChange={(event) => {
              setNext(event.target.value)
              setChanged(false)
            }}
          />
          <p
            id="new-password-help"
            className={cn(
              'text-xs',
              newPasswordIssue === null ? 'text-muted-text' : 'text-danger',
            )}
          >
            {newPasswordIssue ??
              `At least ${String(PASSWORD_MIN_LENGTH)} characters. A long phrase beats a short password with a symbol in it.`}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm-password">Confirm new password</Label>
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            aria-invalid={mismatch}
            onChange={(event) => {
              setConfirm(event.target.value)
              setChanged(false)
            }}
          />
          {mismatch ? (
            <p className="text-xs text-danger">
              The two new passwords do not match.
            </p>
          ) : null}
        </div>

        {error !== null ? <p className="text-sm text-danger">{error}</p> : null}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!canSubmit}>
            {changePassword.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            Change password
          </Button>
          {changed ? (
            <span className="flex items-center gap-1 text-sm text-muted-text">
              <Check className="h-3.5 w-3.5" />
              Password changed
            </span>
          ) : null}
        </div>
      </form>
    </section>
  )
}
